import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Cookies from "js-cookie";
import { setAuthUser } from "../redux/slices/authSlice";
import { setProfileField } from "../redux/slices/profileSlice";
import { getApiErrorMessage } from "../utils/apiError";
import {
  useResendVerificationEmailMutation,
  useResendResetOtpMutation,
  useVerifySignupOTPMutation,
} from "../redux/services/authApi";

type VerifyMode = "signup" | "reset" | "create-account";
/** Where /verify-token says to go next. Null when the API didn't say (older backend). */
export type VerifyNextStep = "company_setup" | "kyc" | "login";
const NEXT_STEPS: VerifyNextStep[] = ["company_setup", "kyc", "login"];

export const useVerifyEmail = (email: string, mode: VerifyMode, countdown = 60) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const normalizedMode: "signup" | "reset" =
    mode === "create-account" ? "signup" : (mode as "signup" | "reset");

  const [otp, setOtp] = useState("");
  const [message, setMessage] = useState("");
  // Success notes (e.g. "code sent") are shown in green, errors in red.
  const [messageType, setMessageType] = useState<"error" | "success">("error");
  const [isCounting, setIsCounting] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(countdown);
  const [isDone, setIsDone] = useState(true);
  const [isResending, setIsResending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [nextStep, setNextStep] = useState<VerifyNextStep | null>(null);

  const [resendSignupOtp] = useResendVerificationEmailMutation();
  const [resendResetOtp] = useResendResetOtpMutation();
  const [verifySignupToken] = useVerifySignupOTPMutation();

  // countdown timer
  useEffect(() => {
    if (!isCounting) return;

    const timer = setTimeout(() => {
      if (secondsLeft > 0) {
        setSecondsLeft((prev) => prev - 1);
      } else {
        setIsCounting(false);
        setIsDone(true);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [isCounting, secondsLeft]);

  // resend otp
  const startCountdown = useCallback(async () => {
    if (!email || isCounting) return;

    try {
      setMessage("");
      setMessageType("error");
      setIsResending(true);

      let res;
      if (normalizedMode === "reset") {
        console.log("Resending reset OTP...");
        res = await resendResetOtp({ email });
      } else {
        console.log("Resending signup token...");
        res = await resendSignupOtp({ email });
      }

      console.log("Resend raw response:", res);
      const data = "data" in res ? res.data : res;
      console.log("Resend normalized response:", data);

      if ("error" in res && res.error) {
        // e.g. 404 no account, 422 already verified, 503 email failed: { error: { message } }
        setMessage(getApiErrorMessage(res.error, "Failed to resend code."));
      } else if (data?.success || data?.status === "success") {
        setMessageType("success");
        setMessage(
          normalizedMode === "reset"
            ? "OTP sent successfully."
            : "Verification token sent successfully."
        );
        setIsCounting(true);
        setIsDone(false);
        setSecondsLeft(countdown);
      } else {
        setMessage(data?.message || "Failed to resend code.");
        console.log("Resend failed:", data);
      }
    } catch (err: any) {
      console.log("Resend API error:", err);
      setMessage(getApiErrorMessage(err, err?.message || "Failed to resend code."));
    } finally {
      setIsResending(false);
    }
  }, [email, isCounting, normalizedMode, resendResetOtp, resendSignupOtp, countdown]);

  // handle for continue/verfify
  const handleContinue = useCallback(async () => {
    if (!otp || otp.length !== 6) {
      const msg =
        normalizedMode === "reset"
          ? "Please enter the 6-digit OTP."
          : "Please enter the 6-digit verification token.";
      setMessage(msg);
      console.log("Validation failed:", msg);
      return;
    }

    if (normalizedMode === "reset") {
      navigate("/password-reset", { state: { email, token: otp } });
    } else {
      try {
        console.log("Sending verifySignupToken request...");
        const res = await verifySignupToken({ email, token: otp });
        setMessageType("error");
        if ("error" in res && res.error) {
          // 401: "Token is invalid" / "Token has expired" / "User verification record not found"
          // 429: more than 5 tries a minute for this email.
          const status = (res.error as any)?.status;
          setMessage(
            status === 429
              ? "Too many attempts. Please wait a minute and try again."
              : getApiErrorMessage(res.error, "Invalid verification token.")
          );
          setShowSuccess(false);
          return;
        }

        const data = "data" in res ? res.data : res;

        if (data?.status === "success" || data?.success) {
          console.log("Signup token verified successfully!");
          // { data: { next_step, user, token } } — save the session the same way
          // signup/login do, so returning users can carry on without logging in.
          const payload = data?.data ?? {};
          const token: string | undefined = payload.token;
          const user = payload.user;
          if (token && user?.id) {
            Cookies.set("token", token, { expires: 7, secure: false, sameSite: "Lax" });
            localStorage.setItem("token", token);
            localStorage.setItem("loginTimestamp", String(Date.now()));
            Cookies.set("signup_user_id", String(user.id));
            dispatch(setAuthUser({ token, user }));
            const isOrg = String(user.account_type || "").toLowerCase().startsWith("org");
            dispatch(setProfileField({ field: "company_type", value: isOrg ? "company" : "individual" }));
          }
          setNextStep(NEXT_STEPS.includes(payload.next_step) ? payload.next_step : null);
          setShowSuccess(true);
          setMessage(""); 
        } else {
          const errMsg = data?.message || "Invalid verification token.";
          console.log("Signup verification failed:", errMsg);
          setMessage(errMsg);
          setShowSuccess(false); 
        }
      } catch (err: any) {
        console.log("API error during signup verification:", err);
        setMessage(getApiErrorMessage(err, "Failed to verify token."));
        setShowSuccess(false);
      }
    }
  }, [otp, normalizedMode, navigate, email, verifySignupToken, dispatch]);

  return {
    otp,
    setOtp,
    message,
    messageType,
    startCountdown,
    isCounting,
    secondsLeft,
    isDone,
    isResending,
    handleContinue,
    normalizedMode,
    showSuccess,
    setShowSuccess,
    nextStep,
  };
};
